import { createMuiTheme } from '@material-ui/core';

const colorPrimary = '#5BBCCC';
const colorSecondary = '#C85024';
const fontPrimary = 'var(--font-family-primary)';
const fontSecondary = 'var(--font-family-secondary)';
const fontScript = 'var(--font-family-script)';

const muiTheme = createMuiTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
    },
  },
  palette: {
    primary: {
      main: colorPrimary,
    },
    secondary: {
      main: colorSecondary,
    },
  },
  props: {
    MuiContainer: {
      fixed: true,
      maxWidth: 'xl',
    },
    MuiPaper: {
      variant: 'outlined',
    },
  },
  typography: {
    fontFamily: fontPrimary,
    button: {
      fontWeight: 800,
    },
    caption: {
      lineHeight: 1.25,
    },
  },
});

// spacing utility
const s = (x: number) => muiTheme.spacing(x);

// typography
muiTheme.typography.body1 = {
  fontSize: '1rem',
  lineHeight: 1.2,
  [muiTheme.breakpoints.up('xl')]: {
    fontSize: '1.2rem',
  },
};
muiTheme.typography.body2 = {
  fontSize: '.9rem',
  lineHeight: 1.2,
  [muiTheme.breakpoints.up('xl')]: {
    fontSize: '1rem',
  },
};
muiTheme.typography.h1 = {
  fontSize: '2rem',
  fontWeight: 800,
  lineHeight: 1.2,
  textTransform: 'uppercase',
  [muiTheme.breakpoints.up('xl')]: {
    fontSize: '2.5rem',
  },
};
muiTheme.typography.h2 = {
  fontSize: '1.5rem',
  fontWeight: 800,
  lineHeight: 1.2,
  textTransform: 'uppercase',
  [muiTheme.breakpoints.up('xl')]: {
    fontSize: '2rem',
  },
};
muiTheme.typography.h3 = {
  fontSize: '1.25rem',
  fontWeight: 800,
  lineHeight: 1.2,
  textTransform: 'uppercase',
  [muiTheme.breakpoints.up('xl')]: {
    fontSize: '1.5rem',
  },
};
muiTheme.typography.h4 = {
  fontSize: '1.1rem',
  fontWeight: 800,
  lineHeight: 1.2,
  textTransform: 'uppercase',
  [muiTheme.breakpoints.up('xl')]: {
    fontSize: '1.25rem',
  },
};
muiTheme.typography.h5 = {
  fontSize: '1rem',
  fontWeight: 800,
  lineHeight: 1.2,
  textTransform: 'uppercase',
  [muiTheme.breakpoints.up('xl')]: {
    fontSize: '1.1rem',
  },
};
muiTheme.typography.h6 = {
  fontSize: '.9rem',
  fontWeight: 800,
  lineHeight: 1.2,
  textTransform: 'uppercase',
  [muiTheme.breakpoints.up('xl')]: {
    fontSize: '1rem',
  },
};

const sectionHeadingBase = {
  fontFamily: fontPrimary,
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '0.02em',
  color: colorSecondary,
};
const typography = muiTheme.typography as unknown as Record<string, unknown>;
  typography.sectionHeading = { ...sectionHeadingBase, fontSize: 'clamp(1.5rem, 4vw, 3rem)' };
  typography.sectionHeadingMedium = { ...sectionHeadingBase, fontSize: 'clamp(1.35rem, 3.5vw, 2.5rem)' };
  typography.sectionHeadingSmall = { ...sectionHeadingBase, fontSize: 'clamp(1.15rem, 3vw, 2rem)' };

const sectionSubtitleBase = {
  fontFamily: fontSecondary,
  fontWeight: 500,
  lineHeight: 1.4,
};
typography.sectionSubtitle = { ...sectionSubtitleBase, fontSize: 'clamp(1rem, 2.5vw, 2rem)' };
typography.sectionSubtitleMedium = { ...sectionSubtitleBase, fontSize: 'clamp(1.125rem, 3vw, 2rem)' };
typography.sectionSubtitleSmall = { ...sectionSubtitleBase, fontSize: 'clamp(0.875rem, 2vw, 1.25rem)' };

// overrides
muiTheme.overrides = {
  MuiPaper: {
    root: {
      padding: s(3),
      backgroundColor: 'transparent',
      [muiTheme.breakpoints.up('lg')]: {
        padding: s(5),
      },
    },
    outlined: {
      border: 'none',
    },
  },
  MuiButton: {
    contained: {
      border: 'none',
      borderRadius: 4,
      padding: '0.75rem 1.25rem',
      fontSize: '0.95rem',
      transition: 'transform 0.2s ease, background-color 0.2s ease',
      '&:hover': {
        transform: 'scale(1.05)',
      },
      [muiTheme.breakpoints.up('md')]: {
        padding: '1rem 2rem',
        fontSize: '1.1rem',
      },
    },
    containedSecondary: {
      backgroundColor: colorSecondary,
      '&:hover': {
        backgroundColor: '#e0622b',
      },
      '&.Mui-disabled': {
        backgroundColor: muiTheme.palette.secondary.light,
      },
      color: 'white !important', // for button links
    },
    containedPrimary: {
      '&:hover': {
        backgroundColor: muiTheme.palette.primary.light,
      },
      color: 'white !important', // for button links
    },
    text: {
      textDecoration: 'underline !important',
    },
  },
  MuiInputBase: {
    root: {
      fontFamily: fontPrimary,
    },
  },
  MuiOutlinedInput: {
    root: {
      borderRadius: 0,
      '& fieldset': {
        borderColor: colorPrimary,
      },
    },
  },
  MuiInputAdornment: {
    root:{
      cursor: 'pointer'
    }
  }
};

export default muiTheme;
